"use client";

/* eslint-disable @next/next/no-img-element -- static Figma vectors at their exact sizes */

import React from "react";
import Image from "next/image";
import { CTACornerButton } from "@/shadcn/ui/corner-button";
import { PageTrademania2ReplaySchema } from "@/sanity/pages/PageTrademania2ReplayTemplate";
import { COUNTDOWN_UNITS, Highlight, useCountdown } from "@/components/trademania-2/shared";
import { Emphasis, StepSection, ctaPhoneLabelClass } from "@/components/trademania-2-replay/shared";

import texture from "@/public/trademania-2-replay/ticket-texture.svg";
import colon from "@/public/trademania-2/count-colon.svg";

/**
 * Figma "Countdown": glass box with four digit tiles; 0.76 of the desktop size on phones.
 * Desktop line boxes are Figma's rendered 45 / 23 (not the 44.3 / 22.2 leading) so the box is 182.
 */
function PrizeCountdown({ label, target }: { label?: string; target?: string }) {
  const parts = useCountdown(target);

  return (
    <div className="flex w-full flex-col items-center gap-[2.25rem] rounded-[6px] border border-white/[.05] bg-white/[.08] px-[2.25rem] pt-[2.75rem] pb-[2.25rem] backdrop-blur-[96.9px] lg:w-[57.125rem]">
      {label && (
        <p className="font-geist text-[2.25rem] leading-[2.25rem] font-medium whitespace-nowrap text-[#F4F4ED]">
          {label}
        </p>
      )}
      <div className="flex items-center gap-[1.33rem] lg:gap-[1.75rem]">
        {COUNTDOWN_UNITS.map(([key, unit], i) => (
          <React.Fragment key={key}>
            {i > 0 && (
              <img
                src={colon.src}
                alt=""
                aria-hidden
                width={5.088}
                height={49.376}
                className="h-[4.691rem] w-[.483rem] lg:h-[6.172rem] lg:w-[.636rem]"
              />
            )}
            <div className="flex flex-col items-center gap-[.95rem] lg:gap-[1.25rem]">
              <span className="flex flex-col items-center rounded-[4.56px] border-[.76px] border-white/[.06] bg-white/[.09] px-[1.33rem] py-[1.14rem] lg:rounded-[6px] lg:border lg:px-[1.75rem] lg:py-[1.5rem]">
                <span className="text-center font-bebas text-[5.32rem] leading-[4.211rem] whitespace-nowrap text-white lg:text-[7rem] lg:leading-[5.625rem]">
                  {parts[key]}
                </span>
              </span>
              <span className="text-center font-geist text-[1.71rem] leading-[2.105rem] font-medium text-white/50 lg:text-[2.25rem] lg:leading-[2.875rem]">
                {unit}
              </span>
            </div>
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

/**
 * Step 2: the raffle ticket. Notches and perforation sit in the gap between copy and passes:
 * left/right at 294 on phones (passes above), top/bottom at 616 from lg up (passes on the right).
 */
export function PrizeStep({ data }: { data: PageTrademania2ReplaySchema }) {
  const paragraphs = (data?.prizeText ?? "").split(/\n\s*\n/).filter((p) => p.trim());

  return (
    <StepSection
      chip={data?.prizeChip}
      title={data?.prizeTitle}
      highlightClassName="bg-[#F6F6F3]"
      className="border-y border-[#0B0C0D]/[.08] bg-white">
      <article className="relative isolate grid w-full gap-y-[4rem] overflow-clip rounded-[8px] bg-[#0B0C0D] p-[3rem] [grid-template-areas:'passes'_'copy'_'count'] lg:w-[150rem] lg:grid-cols-[1fr_65rem] lg:items-center lg:gap-x-[6rem] lg:gap-y-[3.5rem] lg:py-[7rem] lg:pr-[5rem] lg:pl-[7rem] lg:[grid-template-areas:'copy_passes'_'copy_count']">
        {/* CI contour texture, clipped by the ticket */}
        <img
          src={texture.src}
          alt=""
          aria-hidden
          className="pointer-events-none absolute bottom-0 left-[-100.081rem] -z-10 h-[112.177rem] w-[259.039rem] max-w-none lg:top-[-20rem] lg:bottom-auto lg:left-[-47.5rem]"
        />
        {/* perforation */}
        <span
          aria-hidden
          className="absolute top-[36.688rem] right-[3rem] left-[3rem] h-px bg-[repeating-linear-gradient(90deg,rgba(217,249,185,.3)_0_.75rem,transparent_.75rem_1.75rem)] lg:top-[4rem] lg:right-auto lg:bottom-[4rem] lg:left-[77rem] lg:h-auto lg:w-px lg:bg-[repeating-linear-gradient(180deg,rgba(217,249,185,.33)_0_.75rem,transparent_.75rem_1.75rem)]"
        />
        {/* notches in the section colour */}
        <span
          aria-hidden
          className="absolute top-[36.75rem] left-[-2rem] h-[4rem] w-[4rem] -translate-y-1/2 rounded-full bg-white lg:top-[-2.5rem] lg:left-[77rem] lg:h-[5rem] lg:w-[5rem] lg:-translate-x-1/2 lg:translate-y-0"
        />
        <span
          aria-hidden
          className="absolute top-[36.75rem] right-[-2rem] h-[4rem] w-[4rem] -translate-y-1/2 rounded-full bg-white lg:top-auto lg:right-auto lg:bottom-[-2.5rem] lg:left-[77rem] lg:h-[5rem] lg:w-[5rem] lg:-translate-x-1/2 lg:translate-y-0"
        />

        <div className="flex flex-col items-start gap-[2.5rem] [grid-area:copy] lg:gap-[3rem]">
          {data?.prizeCardTitle && (
            <h3 className="font-owners text-[4rem] leading-[1.08] font-medium tracking-[.01em] whitespace-pre-line text-white uppercase lg:text-[5.5rem]">
              <Emphasis
                text={data.prizeCardTitle}
                emphasis={(part) => (
                  <Highlight
                    theme="dark"
                    className="text-[#D9F9B9] italic"
                    boxClassName="inset-x-[-.25em] top-0 bottom-[-.045em] lg:right-[-.34em] lg:left-[-.18em] lg:top-[-.072em] lg:bottom-[-.03em]">
                    {part}
                  </Highlight>
                )}
              />
            </h3>
          )}
          {!!data?.prizePills?.length && (
            <ul className="flex flex-wrap gap-[1.5rem]">
              {data.prizePills.map((pill, i) => (
                <li
                  key={i}
                  className="rounded-full border border-[#D9F9B9]/50 bg-[#D9F9B9]/[.08] px-[2.5rem] py-[1.5rem] font-geist text-[2rem] leading-none font-medium whitespace-nowrap text-[#D9F9B9]">
                  {pill}
                </li>
              ))}
            </ul>
          )}
          {paragraphs.length > 0 && (
            <div className="flex flex-col gap-[1.75rem] font-geist text-[2rem] leading-[1.5] text-white/70 lg:w-[56.125rem]">
              {paragraphs.map((paragraph, i) => (
                <p key={i}>{paragraph}</p>
              ))}
            </div>
          )}
          {data?.prizeCTA && (
            <CTACornerButton
              data={data.prizeCTA}
              className={ctaPhoneLabelClass}
            />
          )}
        </div>

        {/* VIP passes on black, lightened into the ticket (Figma 753 × 443 from -105 / -89) */}
        <div className="relative h-[31.75rem] [grid-area:passes] lg:h-[35.25rem]">
          {data?.prizeImage?.src && (
            <Image
              src={data.prizeImage}
              alt=""
              sizes="(max-width: 992px) 120vw, 40vw"
              className="absolute top-[-3rem] left-[-9.75rem] h-[37.272rem] w-[63.353rem] max-w-none object-cover mix-blend-lighten lg:top-[-11.125rem] lg:left-[-13.125rem] lg:h-[55.375rem] lg:w-[94.125rem]"
            />
          )}
        </div>

        <div className="flex justify-center [grid-area:count]">
          <PrizeCountdown
            label={data?.prizeCountdownLabel}
            target={data?.prizeDeadline}
          />
        </div>
      </article>
    </StepSection>
  );
}
